import React from 'react';
import SafeSEO from '@/components/SafeSEO';
import { motion } from 'framer-motion';
import { Building2, Zap, Factory, Plane, Droplet, Wifi, Truck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Sectors = () => {
  const { t } = useLanguage();

  const sectors = [
    {
      icon: Zap,
      title: t('sectors.items.energy.title'),
      description: t('sectors.items.energy.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/e27aef50c8616f609ce1769b30678eaf.png',
    },
    {
      icon: Building2,
      title: t('sectors.items.realEstate.title'),
      description: t('sectors.items.realEstate.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/1b4ab00b1e14845dcb24ddbbde9015d8.png',
    },
    {
      icon: Truck,
      title: t('sectors.items.infra.title'),
      description: t('sectors.items.infra.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/7a6ca9d4827d03294724d926a50f821e.png',
    },
    {
      icon: Plane,
      title: t('sectors.items.tourism.title'),
      description: t('sectors.items.tourism.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/0b34f365139dca5dfe285912cb0e9a22.png',
    },
    {
      icon: Droplet,
      title: t('sectors.items.water.title'),
      description: t('sectors.items.water.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/050376996ac1982bc5d6056e353839d5.png',
    },
    {
      icon: Wifi,
      title: t('sectors.items.tech.title'),
      description: t('sectors.items.tech.desc'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/5c7222d4a7c26e58ec33709c93c8a05a.png',
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('sectors.title')}
        description={t('sectors.subtitle')}
        url="https://consulogic-gt-com-544311.hostingersite.com/sectores"
      />

      <div className="pt-20">
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('sectors.title')}</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                {t('sectors.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-4">
                {t('sectors.experienceTitle')}
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('sectors.experienceDesc')}
              </p>
            </motion.div>

            <div className="space-y-24">
              {sectors.map((sector, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-xl flex items-center justify-center mb-6">
                      <sector.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#0A5F7F] mb-4">{sector.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{sector.description}</p>
                  </div>
                  <div className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                    <img
                      className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
                      alt={sector.title}
                      src={sector.image} 
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                {t('sectors.integratedTitle')}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('sectors.integratedDesc1')}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t('sectors.integratedDesc2')}
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Sectors;